import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  MessageSquare,
  FileText,
  Users,
  Bell,
  AlertTriangle,
  RefreshCw,
  Eye,
  Shield,
  Activity,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('access_token');

// Action icons mapping
const ACTION_ICONS = {
  'email.send': Mail,
  'sms.send': MessageSquare,
  'task.create': FileText,
  'job.create': Users,
  'job.update': Users,
  'contact.create': Users,
  'contact.update': Users,
  'notification.send': Bell,
};

// Risk tier colors
const RISK_COLORS = {
  safe: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  confirm: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  admin: 'bg-red-500/10 text-red-500 border-red-500/20',
};

// Status colors
const STATUS_COLORS = {
  pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  approved: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  executed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
  expired: 'bg-slate-500/10 text-slate-500 border-slate-500/20',
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const ActionIcon = ({ actionName }) => {
  const Icon = ACTION_ICONS[actionName] || Zap;
  return <Icon className="h-4 w-4" />;
};

export default function ActionApprovals() {
  const navigate = useNavigate();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [dailyLimits, setDailyLimits] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      };

      const [approvalsRes, statsRes, limitsRes, actionsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/actions/pending/all`, { headers }),
        fetch(`${API_URL}/api/actions/stats/daily`, { headers }),
        fetch(`${API_URL}/api/actions/limits`, { headers }),
        fetch(`${API_URL}/api/actions/available`, { headers }),
        fetch(`${API_URL}/api/actions/audit/logs?limit=50`, { headers }),
      ]);

      if (approvalsRes.ok) {
        const data = await approvalsRes.json();
        setPendingApprovals(data.approvals || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setDailyStats(data);
      }

      if (limitsRes.ok) {
        const data = await limitsRes.json();
        setDailyLimits(data.limits);
      }

      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setAvailableActions(data.actions || []);
      }

      if (logsRes.ok) {
        const data = await logsRes.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load action data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleApprove = async (approvalId) => {
    setApproving(true);
    try {
      const response = await fetch(`${API_URL}/api/actions/approval/${approvalId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: 'Approved via dashboard' })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Action approved and executed');
        setShowPreviewModal(false);
        setSelectedApproval(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve action');
      }
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Failed to approve action');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval) return;
    
    setApproving(true);
    try {
      const response = await fetch(`${API_URL}/api/actions/approval/${selectedApproval.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason || 'Rejected via dashboard' })
      });

      if (response.ok) {
        toast.success('Action rejected');
        setShowRejectModal(false);
        setShowPreviewModal(false);
        setSelectedApproval(null);
        setRejectReason('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject action');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject action');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="action-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Action Approvals</h1>
          <p className="text-muted-foreground">
            Review and approve AI agent actions before execution
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold">{pendingApprovals.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Emails Today</p>
                <p className="text-2xl font-bold">
                  {dailyLimits?.['email.send']?.used_today || 0}
                  <span className="text-sm text-muted-foreground font-normal">
                    /{dailyLimits?.['email.send']?.daily_limit || 100}
                  </span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">SMS Today</p>
                <p className="text-2xl font-bold">
                  {dailyLimits?.['sms.send']?.used_today || 0}
                  <span className="text-sm text-muted-foreground font-normal">
                    /{dailyLimits?.['sms.send']?.daily_limit || 50}
                  </span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Actions Today</p>
                <p className="text-2xl font-bold">
                  {dailyStats?.actions_by_type?.reduce((sum, a) => sum + a.total, 0) || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="pending-tab">
            Pending ({pendingApprovals.length})
          </TabsTrigger>
          <TabsTrigger value="actions" data-testid="actions-tab">
            Available Actions
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending" className="space-y-4">
          {pendingApprovals.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-emerald-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">All Clear!</h3>
                <p className="text-muted-foreground text-center">
                  No pending action approvals. AI agents are operating within auto-approved parameters.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((approval) => (
                <Card key={approval.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <ActionIcon actionName={approval.action_name} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{approval.action_display_name}</h3>
                            <Badge className={RISK_COLORS[approval.risk_tier]} variant="outline">
                              {approval.risk_tier}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {approval.preview?.summary}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Agent: {approval.triggered_by_agent_name || 'Unknown'}</span>
                            <span>|</span>
                            <span>Department: {approval.department || 'System'}</span>
                            <span>|</span>
                            <span>{formatDate(approval.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowPreviewModal(true);
                          }}
                          data-testid={`preview-btn-${approval.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleApprove(approval.id)}
                          disabled={approving}
                          data-testid={`approve-btn-${approval.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowRejectModal(true);
                          }}
                          disabled={approving}
                          data-testid={`reject-btn-${approval.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Available Actions Tab */}
        <TabsContent value="actions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Registered Actions</CardTitle>
              <CardDescription>
                These are all actions available to AI agents in the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableActions.map((action) => (
                  <div
                    key={action.name}
                    className="p-4 border rounded-lg hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ActionIcon actionName={action.name} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{action.display_name}</h4>
                          <Badge className={RISK_COLORS[action.risk_tier]} variant="outline" size="sm">
                            {action.risk_tier}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {action.description}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {action.category}
                          </Badge>
                          {action.daily_limit && (
                            <Badge variant="outline" className="text-xs">
                              {action.daily_limit}/day
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Action Audit Log</CardTitle>
              <CardDescription>
                Recent action executions and their results
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No audit logs yet. Actions will appear here when executed.
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <ActionIcon actionName={log.action_name} />
                        <div>
                          <p className="font-medium text-sm">{log.action_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {log.event_type} | Agent: {log.triggered_by_agent_name || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={log.success ? STATUS_COLORS.executed : STATUS_COLORS.rejected}
                          variant="outline"
                        >
                          {log.success ? 'Success' : 'Failed'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ActionIcon actionName={selectedApproval?.action_name} />
              {selectedApproval?.action_display_name} Preview
            </DialogTitle>
            <DialogDescription>
              Review this action before approving or rejecting
            </DialogDescription>
          </DialogHeader>

          {selectedApproval && (
            <div className="space-y-4">
              {/* Warnings */}
              {selectedApproval.preview?.warnings?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-500 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">Warnings</span>
                  </div>
                  <ul className="list-disc list-inside text-sm">
                    {selectedApproval.preview.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Email Preview */}
              {selectedApproval.action_name === 'email.send' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">From</p>
                      <p className="font-medium">{selectedApproval.preview?.details?.from}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">To</p>
                      <p className="font-medium">
                        {selectedApproval.preview?.details?.to?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Subject</p>
                    <p className="font-medium">{selectedApproval.preview?.details?.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Body</p>
                    <div
                      className="border rounded-lg p-4 bg-muted/50 max-h-64 overflow-y-auto"
                      dangerouslySetInnerHTML={{
                        __html: selectedApproval.preview?.details?.body_full || ''
                      }}
                    />
                  </div>
                </div>
              )}

              {/* SMS Preview */}
              {selectedApproval.action_name === 'sms.send' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">To</p>
                    <p className="font-medium">{selectedApproval.preview?.details?.to}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Message</p>
                    <div className="border rounded-lg p-4 bg-muted/50">
                      {selectedApproval.preview?.details?.message}
                    </div>
                  </div>
                </div>
              )}

              {/* Generic Preview */}
              {!['email.send', 'sms.send'].includes(selectedApproval.action_name) && (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <pre className="text-sm whitespace-pre-wrap">
                    {JSON.stringify(selectedApproval.preview?.details || selectedApproval.args, null, 2)}
                  </pre>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">Agent</p>
                  <p className="text-sm font-medium">{selectedApproval.triggered_by_agent_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="text-sm font-medium">{selectedApproval.department || 'System'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="text-sm font-medium">{formatDate(selectedApproval.created_at)}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreviewModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowPreviewModal(false);
                setShowRejectModal(true);
              }}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              onClick={() => handleApprove(selectedApproval?.id)}
              disabled={approving}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              {approving ? 'Approving...' : 'Approve & Execute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Action</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this action
            </DialogDescription>
          </DialogHeader>

          <Textarea
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={approving}
            >
              {approving ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
